import React, { FC } from 'react';
import Helmet from 'react-helmet';

import getCountryPrefix from 'Utils/getCountryPrefix';

import { Course } from '../types';

type Data = {
  [key in 'offer' | 'course' | 'videoObject' | 'product' | 'person']:
    | {
        [key: string]:
          | string
          | { [key: string]: string | { [key: string]: string } };
      }
    | undefined;
};

const getStructuredData = (course: Course) => {
  const description =
    course.about || course.description || course.metaDescription;

  const courseUrl = `${location.origin}${location.pathname}`;

  const person = {
    '@type': 'Person',
    name: course.professor.fullName,
    image: course.professor.profilePictureUrl,
    url: `${location.origin}${course.professor.profileUrl}`,
    jobTitle: course.professor.occupation,
    description: course.professor.bio,
  };

  const company = {
    '@context': 'http://schema.org/',
    '@type': 'Organization',
    name: 'CREHANA',
    logo: 'https://static.crehana.com/static/img/logo/logo-crehana.png',
    url: `https://www.crehana.com/${getCountryPrefix()}/`,
  };

  const data: Data = {
    offer: {
      '@context': 'http://schema.org/',
      '@type': 'Offer',
      price: course.price.toString(),
      priceCurrency: 'USD',
      url: courseUrl,
      priceValidUntil: course.dueDate,
      seller: company,
      availability: 'InStock',
    },

    course: {
      '@context': 'http://schema.org/',
      '@type': 'Course',
      name: course.title,
      description,
      provider: company,
    },
    product: {
      '@context': 'http://schema.org/',
      '@type': 'Product',
      productID: `crehana-course-${course.originalId}`,
      image: course.promoImage,
      name: course.title,
      description,
      url: courseUrl,
      brand: company,
      manufacturer: {
        '@context': 'http://schema.org/',
        '@type': 'Organization',
        name: 'CREHANA',
        logo: 'https://static.crehana.com/static/img/logo/logo-crehana.png',
        url: `https://www.crehana.com/${getCountryPrefix()}/`,
      },
      category: course.categoryObj?.name || '',
      offers: {
        '@context': 'http://schema.org/',
        '@type': 'Offer',
        url: courseUrl,
        price: course.price.toString(),
        priceCurrency: 'USD',
        seller: company,
        availability: 'InStock',
        priceValidUntil: course.dueDate,
      },
      sku: `Course-${course.originalId}`,
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: course.score.average.toString(),
        reviewCount: course.score.total.toString(),
        bestRating: '5',
        worstRating: '1',
      },
    },
    person,
    videoObject: undefined,
  };

  if (course.trailerSource) {
    const trailerData = course.trailerSource.find(
      source => source.videoSourceType === 'HLSV4',
    );

    if (trailerData) {
      data.videoObject = {
        '@context': 'http://schema.org/',
        '@type': 'VideoObject',
        url: courseUrl,
        thumbnailUrl: course.promoImage || trailerData.imageUrl || '',
        embedURL: courseUrl,
        discussionUrl: `${courseUrl}#comments`,
        name: course.title,
        description,
        height: '405',
        width: '720',
        playerType: 'HTML5 Flash',
        videoQuality: 'HD',
        dateCreated: course.launchDate,
        uploadDate: course.modifiedDate,
        provider: {
          '@context': 'http://schema.org/',
          '@type': 'Organization',
          name: 'CREHANA',
          logo:
            'https://static.crehana.com/static/img/apple-touch-icon-120.0ebc9eb1389a.png',
          url: `https://www.crehana.com/${getCountryPrefix()}/`,
        },
        thumbnail: {
          '@type': 'ImageObject',
          image: trailerData.imageUrl || '',
          url: trailerData.imageUrl || '',
          width: '720',
          height: '405',
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: course.score.average.toString(),
          reviewCount: course.score.total.toString(),
          bestRating: '5',
          worstRating: '1',
        },
      };
    } else {
      // remove the undefined key-value if trailerData doesn't exists
      delete data.videoObject;
    }
  }

  return data;
};

const getReviewQuery = (course: Course) => `
query reviews {
  reviews(course: ${course.originalId}, first: 1) {
    edges {
      node {
        originalId
        comment
        score
        dateCreated
        user {
          fullName
          username
          profileUrl
          thumbnailUrl
        }
      }
    }
  }
}

`;

type SEOStructuredDataProps = {
  course: Course;
};

const SEOStructuredData: FC<SEOStructuredDataProps> = ({ course }) => {
  const [dataState, setDataState] = React.useState<{
    data?: Data;
    loading: boolean;
  }>({
    data: undefined,
    loading: true,
  });

  React.useEffect(() => {
    if (!course) {
      return;
    }

    const structuredData = getStructuredData(course);

    const query = getReviewQuery(course);

    // we need to get the first review of the current course to fill the data for product.review
    fetch('/api/v2/graph/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query }),
    })
      .then(res => res.json())
      .then(response => {
        if (
          response &&
          response.data &&
          response.data.reviews.edges.length > 0 &&
          structuredData.product
        ) {
          const review = response.data.reviews.edges[0].node;

          structuredData.product.review = {
            '@type': 'Review',
            reviewRating: {
              '@type': 'Rating',
              ratingValue: review.score.toString(),
              worstRating: '4',
              bestRating: '5',
            },
            reviewBody: review.comment,
            author: {
              '@type': 'Person',
              name: review.user.fullName || review.user.username,
              image: review.user.thumbnailUrl,
              url: `${location.origin}${review.user.profileUrl}`,
            },
            datePublished: review.dateCreated,
          };
        }
        setDataState({
          loading: false,
          data: structuredData,
        });
      })
      .catch(error => {
        console.error(error);
        setDataState({
          loading: false,
          data: structuredData,
        });
      });
  }, []);

  if (dataState.loading || !dataState.data) {
    return null;
  }

  return (
    <Helmet>
      {Object.keys(dataState.data).map((key, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify((dataState.data as Data)[key])}
        </script>
      ))}
    </Helmet>
  );
};

export default SEOStructuredData;
